import React, { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardContent, Divider, Switch } from "@mui/material";
import {
	BlockTitle,
	CustomButton,
	createNotification,
	CustomDialog,
	IconComponent,
	icon,
} from "../../../../common/components";
import { ApiService, BackOfficeService, AiAgentsService } from "../../../../api";
import { translate } from "../../../../common/providers";
import styles from "./DSIPlatform.module.css";
import { useApi } from "../../../../common/hooks";

const WarningMessage = ({ healthyCondition = true, isSevere = false, count = 0, message = "" }) => (
	<div className={styles.warningDescription} data-is-healthy={healthyCondition} data-is-severe={isSevere}>
		<div>
			<IconComponent
				color={(healthyCondition && "var(--lime-500)") || `var(--color-${(isSevere && "red") || "orange"})`}
				icon={icon[(healthyCondition && "faCheck") || "faExclamationTriangle"]}
				size="lg"
			/>
		</div>
		<div>
			{translate(message, {
				count,
			})}
		</div>
	</div>
);

export default function DSIPlatform() {
	const cancelTokenSourceRef = useRef(null);
	const [openClearCache, setOpenClearCache] = useState(false);
	const [openEmailDatabaseIntegrity, setOpenEmailDatabaseIntegrity] = useState(false);
	const [openForceUpdateMailjet, setOpenForceUpdateMailjet] = useState(false);
	const [openForceUpdateNormDatabase, setOpenForceUpdateNormDatabase] = useState(false);
	const [openForceSendDailyNotifs, setOpenForceSendDailyNotifs] = useState(false);
	const [openForceSendWeeklyNotifs, setOpenForceSendWeeklyNotifs] = useState(false);
	const [killSwitchValue, setKillSwitchValue] = useState(false);
	const [healthCounters, setHealthCounters] = useState({ deprecatedAgent: 0, allTools: 0, agentHavingNoTool: 0 });
	const hasDeprecated = useMemo(() => healthCounters.deprecatedAgent > 0, [healthCounters]);
	const hasNoTool = useMemo(() => healthCounters.agentHavingNoTool > 0, [healthCounters]);

	const { call: getAgentsHealth } = useApi(BackOfficeService.getAgentsHealth);
	useEffect(() => {
		if (!cancelTokenSourceRef.current) {
			cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		}
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		AiAgentsService.getKillSwitchStatus(cancelTokenSourceRef.current.token)
			.then((data) => {
				setKillSwitchValue(data.value);
			})
			.catch((err) => console.error(err));
		getAgentsHealth()
			.then(setHealthCounters)
			.catch((err) => console.error(err));
	}, [getAgentsHealth]);
	const handleOpenClearCacheDialog = () => setOpenClearCache((prev) => !prev);
	const clearCache = () =>
		BackOfficeService.clearCache(cancelTokenSourceRef.current.token)
			.then(() => {
				handleOpenClearCacheDialog();
				createNotification({ message: translate("dsi.platform-management.clear-cache.success") });
			})
			.catch((err) => console.error(err));
	const handleOpenEmailDatabaseIntegrityDialog = () => setOpenEmailDatabaseIntegrity((prev) => !prev);
	const emailDatabaseIntegrity = () =>
		BackOfficeService.sendEmailDatabaseIntegrity(cancelTokenSourceRef.current.token)
			.then((data) => {
				handleOpenEmailDatabaseIntegrityDialog();
				createNotification({
					message: translate("dsi.platform-management.send-mail-database-integrity.success", {
						docs: data.value,
					}),
				});
			})
			.catch((err) => console.error(err));
	const handleOpenForceUpdateMailjetDialog = () => setOpenForceUpdateMailjet((prev) => !prev);
	const forceUpdateMailjet = () =>
		BackOfficeService.forceUpdateMailjetUserList(cancelTokenSourceRef.current.token)
			.then((data) => {
				handleOpenForceUpdateMailjetDialog();
				createNotification({
					message: translate("dsi.platform-management.force-update-mailjet.success", { docs: data.value }),
				});
			})
			.catch((err) => console.error(err));
	const handleOpenForceUpdateNormDatabaseDialog = () => setOpenForceUpdateNormDatabase((prev) => !prev);
	const forceUpdateNormDatabase = () =>
		BackOfficeService.forceNormCheck(cancelTokenSourceRef.current.token)
			.then((data) => {
				handleOpenForceUpdateNormDatabaseDialog();
				createNotification({
					message: translate("dsi.platform-management.force-update-norm-database.success", {
						docs: data.value,
					}),
				});
			})
			.catch((err) => console.error(err));
	const handleOpenForceSendDailyNotifsDialog = () => setOpenForceSendDailyNotifs((prev) => !prev);
	const forceSendDailyNotifs = () =>
		BackOfficeService.forceSendDailyNotifs(cancelTokenSourceRef.current.token)
			.then(() => {
				handleOpenForceSendDailyNotifsDialog();
				createNotification({
					message: translate("dsi.platform-management.force-send-daily-notifs.success"),
				});
			})
			.catch((err) => console.error(err));

	const handleOpenForceSendWeeklyNotifsDialog = () => setOpenForceSendWeeklyNotifs((prev) => !prev);
	const forceSendWeeklyNotifs = () =>
		BackOfficeService.forceSendWeeklyNotifs(cancelTokenSourceRef.current.token)
			.then(() => {
				handleOpenForceSendWeeklyNotifsDialog();
				createNotification({
					message: translate("dsi.platform-management.force-send-weekly-notifs.success"),
				});
			})
			.catch((err) => console.error(err));
	const handleChangeKillSwitchValue = () => {
		setKillSwitchValue((prev) => !prev);
		AiAgentsService.updateKillSwitchStatus({ value: !killSwitchValue }, cancelTokenSourceRef.current.token).catch(
			console.error
		);
	};
	return (
		<div className={styles.main}>
			<BlockTitle>{translate("dsi.platform-management.title")}</BlockTitle>
			<div className={styles.content}>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.application-cache")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenClearCacheDialog}>
								{translate("dsi.platform-management.clear-cache")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.database-integrity")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenEmailDatabaseIntegrityDialog}>
								{translate("dsi.platform-management.send-mail-database-integrity")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.update-mailjet")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenForceUpdateMailjetDialog}>
								{translate("dsi.platform-management.force-update-mailjet")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.update-norm-database")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenForceUpdateNormDatabaseDialog}>
								{translate("dsi.platform-management.force-update-norm-database")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.send-daily-notifs")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenForceSendDailyNotifsDialog}>
								{translate("dsi.platform-management.force-send-daily-notifs")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.send-weekly-notifs")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<CustomButton className={styles.button} onClick={handleOpenForceSendWeeklyNotifsDialog}>
								{translate("dsi.platform-management.force-send-weekly-notifs")}
							</CustomButton>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.ai-connections")}
						</span>
						<Divider />
						<div className={styles.cardBody}>
							<div className={styles.switchContainer}>
								<span className={styles.switchText}>
									{translate("dsi.platform-management.agents-workflows")}
								</span>
								<Switch checked={killSwitchValue} onClick={handleChangeKillSwitchValue} />
							</div>
						</div>
					</CardContent>
				</Card>
				<Card className={styles.card}>
					<CardContent className={styles.cardContent}>
						<span className={styles.cardContent__title}>
							{translate("dsi.platform-management.agents-healthcheck")}
						</span>
						<Divider />
						<div className={styles.switchContainer}>
							<span className={`${styles.switchText} ${styles["switchText--padding-vertical"]}`}>
								{translate("dsi.platform-management.agents-healthcheck.tools-counter", {
									count: healthCounters?.allTools,
								})}
							</span>
						</div>
						<div className={styles.warnings}>
							{!hasNoTool && !hasDeprecated && (
								<WarningMessage
									healthyCondition={!hasNoTool && !hasDeprecated}
									isSevere={false}
									message="dsi.platform-management.agents-healthcheck.healthy"
								/>
							)}
							{hasNoTool && (
								<WarningMessage
									isSevere
									count={healthCounters?.agentHavingNoTool}
									healthyCondition={false}
									message={`dsi.platform-management.agents-healthcheck.has-no-tool.${
										(healthCounters?.agentHavingNoTool > 1 && "plural") || "singular"
									}`}
								/>
							)}
							{hasDeprecated && (
								<WarningMessage
									count={healthCounters?.deprecatedAgent}
									healthyCondition={false}
									isSevere={false}
									message={`dsi.platform-management.agents-healthcheck.has-deprecated.${
										(healthCounters?.deprecatedAgent > 1 && "plural") || "singular"
									}`}
								/>
							)}
						</div>
					</CardContent>
				</Card>
			</div>
			<CustomDialog
				maxWidth="xs"
				open={openClearCache}
				subTitle={translate("dsi.platform-management.clear-cache.confirm")}
				title={translate("dsi.platform-management.clear-cache")}
				onClose={handleOpenClearCacheDialog}
				onSubmit={clearCache}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openEmailDatabaseIntegrity}
				subTitle={translate("dsi.platform-management.send-mail-database-integrity.confirm")}
				title={translate("dsi.platform-management.send-mail-database-integrity")}
				onClose={handleOpenEmailDatabaseIntegrityDialog}
				onSubmit={emailDatabaseIntegrity}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openForceUpdateMailjet}
				subTitle={translate("dsi.platform-management.force-update-mailjet.confirm")}
				title={translate("dsi.platform-management.force-update-mailjet")}
				onClose={handleOpenForceUpdateMailjetDialog}
				onSubmit={forceUpdateMailjet}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openForceUpdateNormDatabase}
				subTitle={translate("dsi.platform-management.force-update-norm-database.confirm")}
				title={translate("dsi.platform-management.force-update-norm-database")}
				onClose={handleOpenForceUpdateNormDatabaseDialog}
				onSubmit={forceUpdateNormDatabase}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openForceSendDailyNotifs}
				subTitle={translate("dsi.platform-management.force-send-daily-notifs.confirm")}
				title={translate("dsi.platform-management.force-send-daily-notifs")}
				onClose={handleOpenForceSendDailyNotifsDialog}
				onSubmit={forceSendDailyNotifs}
			/>
			<CustomDialog
				maxWidth="xs"
				open={openForceSendWeeklyNotifs}
				subTitle={translate("dsi.platform-management.force-send-weekly-notifs.confirm")}
				title={translate("dsi.platform-management.force-send-weekly-notifs")}
				onClose={handleOpenForceSendWeeklyNotifsDialog}
				onSubmit={forceSendWeeklyNotifs}
			/>
		</div>
	);
}
